import {APP, CONNECTION_CHANGED, CONNECTION_TYPE_CHANGED, LANGUAGE_CHANGED, SET_ADMIN_MODE, SET_ANONYMOUS_MODE,
	SET_IS_ONLINE, SET_MAP_PROVIDER, SET_PALETTE, SET_SHOW_NEWSLETTER_OPT_IN, SET_SHOW_WIKI, SET_SOUND_SETTINGS_OPEN,
	SET_THEME, TOGGLE_DISCONNECTED_SOUND, TOGGLE_PRESENTATION_MODE, WS_DATA} from "../redux/constants/app.constants";
import {consoleError} from "./log";
import {scrubValue} from "./scrub";

// Navigation/UI-only actions with no personal data. Everything else that is dispatched
// only ever reaches Sentry as `{type}` - see actionTransformer below.
const ACTIONS_WITH_PAYLOAD = [
	CONNECTION_CHANGED,
	CONNECTION_TYPE_CHANGED,
	SET_IS_ONLINE,
	LANGUAGE_CHANGED,
	SET_THEME,
	SET_PALETTE,
	SET_MAP_PROVIDER,
	TOGGLE_PRESENTATION_MODE,
	SET_ADMIN_MODE,
	SET_SHOW_WIKI,
	SET_SOUND_SETTINGS_OPEN,
	TOGGLE_DISCONNECTED_SOUND,
	SET_ANONYMOUS_MODE,
	SET_SHOW_NEWSLETTER_OPT_IN,
];

// Reads a value from an Immutable Map/Record or a plain object without converting the slice.
const read = (obj, key) => {
	if (!obj) return undefined;
	if (typeof obj.get === 'function') return obj.get(key);
	return obj[key];
};

const sizeOf = value => {
	if (!value || typeof value !== 'object') return undefined;
	if (typeof value.size === 'number') return value.size;
	if (Array.isArray(value)) return value.length;
	return Object.keys(value).length;
};

// Reduces any non-APP slice to a size indicator only - no records ever leave the app.
// Works on the Immutable slices directly so that reporting an error never deep-clones the store.
const summarizeSlice = slice => {
	if (!slice || typeof slice !== 'object') return {count: 0};
	const count = sizeOf(read(slice, 'data')) ?? sizeOf(read(slice, 'list')) ?? sizeOf(slice) ?? 0;
	return {count};
};

// noinspection JSUnusedGlobalSymbols
export const sentryOptions = {
	// No getUserContext on purpose: identity is set explicitly (and allowlisted) by
	// identifySentrySaga and must never be derived from state.
	filterBreadcrumbActions: action => {
		try {
			if (action.noBreadcrumb) return false;
			// websocket payloads carry alarm/position/device data
			if (typeof action.type === 'string' && action.type.indexOf(WS_DATA) !== -1) return false;
			const json = JSON.stringify(action);
			if (/password|token|email|phone|latitude|longitude/i.test(json)) return false;
			return true;
		} catch (e) {
			consoleError(e);
			return false;
		}
	},
	stateTransformer: state => {
		if (!state) return {};
		const appSlice = read(state, APP);
		const currentUser = read(appSlice, 'currentUser');
		const modules = read(currentUser, 'modules');
		const result = {
			[APP]: {
				isConnected: read(appSlice, 'isConnected'),
				isOnline: read(appSlice, 'isOnline'),
				wsInit: read(appSlice, 'wsInit'),
				currentUser: currentUser ? {
					id: read(currentUser, 'id'),
					accountId: read(currentUser, 'accountId'),
					userType: read(currentUser, 'userType'),
					accountType: read(currentUser, 'accountType'),
					modules: modules && typeof modules.toJS === 'function' ? modules.toJS() : modules,
					language: read(currentUser, 'language'),
					version: read(currentUser, 'version'),
				} : null,
			},
		};
		// Every other slice (registered or injected) only ever contributes a count.
		const keys = typeof state.keySeq === 'function' ? state.keySeq().toArray() : Object.keys(state);
		keys.forEach(key => {
			if (key === APP) return;
			result[key] = summarizeSlice(read(state, key));
		});
		return scrubValue(result);
	},
	actionTransformer: action => {
		// redux-sentry-middleware calls this with null before the first dispatch
		if (!action || !action.type) return {type: '[none]'};
		if (ACTIONS_WITH_PAYLOAD.indexOf(action.type) === -1) {
			return {type: action.type};
		}
		let json = "[circular structure]";
		try {
			json = JSON.stringify(action);
		} catch (e) {
			//ignore
		}
		if (json.length > 2000) {
			return {
				type: action.type,
				truncatedPayload: json.substring(0, 100)
			};
		}
		return scrubValue(action);
	}
};
